import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'src/app/rootStore';
import { fetchNextStepData, fetchStreamData } from 'src/components/SproutsAgent/utils';
import { DEEPGRAM_STT_URL } from 'src/constants/httpEndPoints';
import { useProfile } from 'src/hooks/useProfile';
import { sproutsAgentSelector } from 'src/store/sprouts-agent/sprouts-agent.selector';
import { addNewMessage } from 'src/store/sprouts-agent/sprouts-agent.slice';

interface AudioVisualizerProps {
  audioRef: any;
  handleTimeUpdateRef: any;
  abortControllerRef: any;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  abortControllerRef,
  audioRef,
  handleTimeUpdateRef,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { customerId, emailId, clientSecret } = useProfile();
  const { isAgentSpeaking, chatStreaming, checkpoint, threadId, audioVisualizerOpen } =
    useSelector(sproutsAgentSelector);
  const [transcribedText, setTranscribedText] = React.useState('');
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const socketRef = React.useRef<WebSocket | null>(null);
  const silenceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const microphoneStreamRef = React.useRef<MediaStream | null>(null);
  const lastTranscriptRef = React.useRef<string[]>([]);
  const [recording, setRecording] = React.useState<boolean>(true);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;

      const socket = new WebSocket(DEEPGRAM_STT_URL, ['token', process.env.DEEPGRAM_API_KEY || '']);
      socketRef.current = socket;

      socket.onopen = () => {
        mediaRecorder.addEventListener('dataavailable', (event) => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(event.data);
          }
        });
        if (microphoneStreamRef.current) {
          visualize();
        }
        setTranscribedText('');
        mediaRecorder.start(250);
        setRecording(true);
      };

      let fullTranscript = [];
      let lastUtterance = '';
      socket.onmessage = (message) => {
        const received = JSON.parse(message.data);
        const transcript = received.channel.alternatives[0]?.transcript?.trim() || '';
        const is_utterance_end = received?.is_final;
        if (transcript) {
          if (transcript && is_utterance_end) {
            if (transcript !== lastUtterance) {
              lastUtterance = transcript;
              fullTranscript.push(transcript);
              setTranscribedText(fullTranscript.join(' '));
            }
          }
          resetSilenceTimer();
        }
      };
      socket.onerror = (error) => console.error('WebSocket error:', error);
      socket.onclose = () => {
        // !chatStreaming && !isAgentSpeaking && startRecording();
      };
    } catch (error) {
      console.error('Microphone access error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }

    setRecording(false);
  };

  const resetSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    silenceTimerRef.current = setTimeout(() => {
      stopRecording();
    }, 3000);
  };

  const visualize = () => {
    if (!canvasRef.current) {
      console.error('Canvas reference is null');
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get 2D context for canvas');
      return;
    }

    if (!microphoneStreamRef.current) {
      console.warn('Microphone stream not available yet');
      return;
    }

    // Set up Web Audio API
    let audioContext: AudioContext;
    let analyser: AnalyserNode;
    let source: MediaStreamAudioSourceNode;
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      source = audioContext.createMediaStreamSource(microphoneStreamRef.current);
      source.connect(analyser);
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (!canvasRef.current || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        analyser.getByteFrequencyData(dataArray);

        const barWidth = 4;
        const barGap = 3;
        const maxBarHeight = canvas.height * 0.8;
        const minBarHeight = canvas.height * 0.1;
        let x = 0;
        const numBars = 30;
        const step = Math.floor(bufferLength / numBars);

        for (let i = 0; i < numBars; i++) {
          const frequencyValue = dataArray[i * step] || 0;
          const barHeight = Math.max((frequencyValue / 255) * maxBarHeight, minBarHeight);
          ctx.fillStyle = '#333333';
          ctx.fillRect(x, canvas.height / 2 - barHeight / 2, barWidth, barHeight);
          x += barWidth + barGap;
        }

        animationFrameRef.current = requestAnimationFrame(draw);
      };
      draw();
    } catch (error) {
      console.error('Error setting up Web Audio API:', error);
      return;
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContext) {
        audioContext.close().catch((err) => console.error('Error closing AudioContext:', err));
      }
    };
  };

  const submitHandler = () => {
    if (!transcribedText) {
      return;
    }
    dispatch(
      addNewMessage({
        content: transcribedText,
        role: 'user',
        contentType: 'simple-text',
      }),
    );
    fetchStreamData(
      true,
      dispatch,
      emailId || '',
      customerId,
      threadId,
      audioRef,
      handleTimeUpdateRef,
      abortControllerRef,
      undefined,
      transcribedText,
      clientSecret,
      checkpoint?.length > 0,
    );
  };

  React.useEffect(() => {
    if (!chatStreaming && !isAgentSpeaking) {
      startRecording();
    } else {
      stopRecording();
    }
  }, [chatStreaming, isAgentSpeaking]);

  React.useEffect(() => {
    if (!recording) {
      submitHandler();
      setTranscribedText('');
      lastTranscriptRef.current = [];
    }
  }, [recording]);

  return (
    <div className="voice-action-visual">
      <canvas ref={canvasRef} width="238" height="40" style={{ marginLeft: '242px' }}></canvas>
    </div>
  );
};

export default AudioVisualizer;
